import React, { useEffect, useState, useMemo } from 'react';
import {
  User, Search, Plus,
  Edit3, Trash2, Phone,
  Mail, Calendar, DollarSign,
  ChevronDown,Loader, Euro,
  X, CheckCircle,UserPlus
} from 'lucide-react';
import { useAlert } from '../hooks/useAlert';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { baseParent, baseStudent, baseTeacher, Parent, routes_per_type, Student, Teacher } from '../components/interfaces/All';
import { calculateDebtPerStudent } from './Wallet.tsx'
// Sample data - replace with Supabase fetch
const studentsTemplates: Student[] = [ baseStudent, baseStudent ];

interface StudentsListInterface {
  account_type: 'student'| 'parent' | 'teacher';
}

const StudentsList: React.FC<StudentsListInterface> = ({account_type =  'student'}) => {
  const navigate = useNavigate();
  const { showAlert, AlertComponent } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [editingPayment, setEditingPayment] = useState<{ studentId: string; amount: string } | null>(null);

  const { state, fetchStudents,  fetchParents, fetchTeachers,
    addCashPayment, fetchMyLedger,
    deleteStudent, deleteParent, deleteTeacher,
    toggleStudentStatus
  } = useSupabase();

  const templates = {
    student: (): Student => ( baseStudent ),
    parent: (): Parent => (baseParent),
    teacher: (): Teacher => (baseTeacher)
  };

  let students = [templates[account_type as keyof typeof templates]()]


  switch(account_type){
    case('student'):
      students = state.students as Student[];
      break;
    case('parent'):
      students = state.parents as Parent[];
    break;
    case('teacher'):
      students = state.teachers as Teacher[];
    break;
  }

  useEffect(() => {
    switch(account_type){
      case('student'):
        fetchStudents();
        fetchMyLedger()
        break;
      case('parent'):
        fetchParents();
        break;
      case('teacher'):
        fetchTeachers();
        fetchMyLedger()
        break;
    }

  }, [account_type]);

  // const filteredStudents = students;
  const filteredStudents = students.filter(student =>
    // student.first_name.includes(searchTerm) ||
    student.email.includes(searchTerm)
  );


  const handleAction = async (studentId: string, action: 'delete' | 'toggle' | 'view-ledger'| 'cash-payment') => {
    setLoadingIds(prev => new Set([...prev, studentId]));
    setOpenDropdownId(null);

    if (action === 'cash-payment') {
      setEditingPayment({ studentId, amount: '' });
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
      return;
    }

    if (action === 'delete') {
      if (window.confirm("Are you sure?")) {
        switch(account_type){
          case 'student':
            deleteStudent(studentId);
            break;
          case 'parent':
            deleteParent(studentId)
            break;
          case 'teacher':
            deleteTeacher(studentId)
            break;
        }
      }
    }

    if (action === 'toggle') {
      toggleStudentStatus(studentId, students.find(s => s.id === studentId)?.status || 'active');
    }

    try {
      // Supabase calls here:
      // await supabase.from('students').update({...}).eq('id', studentId);
      console.log(`Student ${studentId}: ${action}`);

      if (action === 'view-ledger') {
        navigate(routes_per_type[account_type] + `/${studentId}/ledger`);
      }
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }
  };

  const getStatusColor = (status: Student['status']) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20';
      case 'paused': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-800';
    }
  };

  const handleCashPayment = async (studentId: string, amount: number) => {
    if (!amount || amount <= 0) return;

    setLoadingIds(prev => new Set([...prev, studentId]));
    setEditingPayment(null);

    try {
      await addCashPayment(studentId, amount);
      // console.log(`✅ Cash payment €${amount} logged for ${studentId}`);
    } catch (error) {
      console.error('Payment failed:', error);

      showAlert('Payment not recorded!', {
        type: 'warning',
        title: 'Warning!'
      });
      // Show toast/error UI
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }
  };

  // Calculate balance for editing payment
  const balance = (() => {
    const student = students?.find(s => s.id === editingPayment?.studentId);
    return student && 'balance' in student ? student.balance : 0;
  })();

  return (
    <div className="fade-in max-w-md mx-auto pt-4 min-h-screen bg-gradient-to-b">
      {AlertComponent}
      {/* Header */}

      <div className="flex items-center gap-5  mb-4 ps-2 pe-2">
        <User className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" />
        <div>
          <h1 className="text-xl font-bold">
            { account_type == 'student'? 'Students':
              account_type == 'teacher'? 'Teachers':
              account_type == 'parent'? 'Parents': 'Accouints'
            }
          </h1>
          <p className="text-text-light text-sm">
            {students.length}
            { account_type == 'student'? ' Students':
              account_type == 'teacher'? ' Teachers':
              account_type == 'parent'? ' Parents': ' Accouints'
            }
          </p>
        </div>
        {/* button (add new) */}
        <div className="ml-auto">
          <button
            className="btn bg-white dark:bg-gray-700 sm:block p-2 rounded-full p-2 items-center shadow-md hoover:shadow-lg"
            onClick={() => navigate(routes_per_type[account_type] +'/new')}
          >
            <UserPlus className="w-6 h-6 mr-0 " />
          </button>
        </div>
      </div>

      <div className="mb-8">
        {/* Search */}
        <div className="card p-4">
          <div className="relative">
            <Search className="w-5 h-5 text-text-light absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search students by name or email..."
              className="w-full pl-12 pr-4 py-3 border border-border rounded-xl bg-background text-text focus:ring-2 focus:ring-primary focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Students Grid */}
      <div className="grid gap-4">
        {filteredStudents.length === 0 ? (
          <div className="card p-16 text-center">
            <User className="w-20 h-20 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-bold mb-2 text-text-light">
              No { account_type == 'student'? 'students':
                account_type == 'teacher'? 'teachers':
                account_type == 'parent'? 'parents': 'accounts'
              } found
            </h3>
            <p className="text-text-light mb-6">Try adjusting your search or add your first
              { account_type == 'student'? ' student':
                account_type == 'teacher'? ' teacher':
                account_type == 'parent'? ' parent': 'account'
              }.</p>
            <button className="btn btn-primary" onClick={() => navigate( routes_per_type[account_type] + '/new')}>
              Add First { account_type == 'student'? 'Student':
                account_type == 'teacher'? 'Teacher':
                account_type == 'parent'? 'Parent': 'Account'
              }
            </button>
          </div>
        ) : (
          filteredStudents.map((student) => (
            <div

              key={student.id}
              className="card group hover:shadow-sm transition-all border-1 border-secondary "
            >
              {/* Student header */}
              <div
                className="flex items-start justify-between p-3 pb-4">
                
                <div className="flex items-start gap-2 flex-1">
                  <div className="w-10 rounded-3xl bg-blue-200">
                    <img className="w-10 rounded-3xl" src={student.picture}/>
                  </div>
                  {/* <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <User className="w-7 h-7 text-primary" />
                  </div> */}

                  <div
                    className="flex-1 min-w-0"
                    onClick={() => navigate(routes_per_type[account_type] +`/${student.id}`)}>
                    <h3 className=" font-bold text-text truncate">{student.first_name} {student.last_name}</h3>
                    <p className="text-text-light text-xs truncate">{student.email}</p>
                    {/*

                    {student.phone && (
                      <p className="text-sm text-text-light flex items-center gap-1 mt-1">
                        <Phone className="w-4 h-4" />
                        {student.phone}
                      </p>
                    )} */}

                    {/* <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{student.totalLessons} lessons</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      <span>Last: {student.lastLesson}</span>
                    </div> */}


                  </div>
                </div>

                {/* Status & Balance */}
                {/* <div
                  className="text-center ml-2 flex-shrink-0"
                  onClick={() => navigate(routes_per_type[account_type] + `/${student.id}`)}
                >
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                      student.status
                    )} mb-2`}
                  >
                    {student.status.toUpperCase()}
                  </span>

                  {'balance' in student && (() => {
                    const owing = studentsOwing.find(so => so.student?.id === student.id);
                    console.log('owing',owing)
                    const amount = owing?.amountOwed ?? 0;
                    const isOwing = amount > 0;

                    return (
                      <div
                        className={`font-bold p-2 px-3 rounded-lg transition-colors ${
                          isOwing
                            ? 'text-red-600 dark:text-red-400 bg-red-50/50 dark:bg-red-900/20'
                            : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/20'
                        }`}
                      >
                        {isOwing
                          ? `€${amount}`
                          : `Paid(€${Math.abs(amount)})`}
                      </div>
                    );
                  })()}
                </div> */}

                {/* Action buttons */}
                <div className="flex gap-2 ml-4 pt-0">
                  <div className="relative">
                    <button
                      className="btn btn-outline px-2 py-2.5 text-sm flex items-center gap-2"
                      onClick={() => setOpenDropdownId(openDropdownId === student.id ? null : student.id)}
                    >
                      Set
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdownId === student.id ? 'rotate-180' : ''}`} />
                    </button>

                    { openDropdownId === student.id && (
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 border border-border rounded-xl shadow-sm py-1 z-50">
                        <button
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-b border-gray-100 dark:border-gray-700 first:rounded-t-xl bg-emerald-50/50"
                          onClick={() => handleAction(student.id, 'cash-payment')}
                          disabled={loadingIds.has(student.id)}
                        >
                          <Euro className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          Add Payment
                        </button>

                        { account_type == 'student' && (
                          <button
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all first:rounded-t-xl"
                            onClick={() => handleAction(student.id, 'view-ledger')}
                          >
                            <DollarSign className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            View Ledger
                          </button>
                        )}
                        <button
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                          onClick={() => navigate(routes_per_type[account_type] + `/${student.id}/edit`)}
                        >
                          <Edit3 className="w-4 h-4 flex-shrink-0" />
                          { 'Edit'+ ' ' + account_type }
                        </button>
                        <button
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border-t border-gray-100 dark:border-gray-700"
                          onClick={() => handleAction(student.id, 'toggle')}
                        >
                          { student.status === 'active' ? (
                            <>
                              <User className="w-4 h-4 opacity-50 flex-shrink-0" />
                              { 'Pause'+ ' ' + account_type }
                            </>
                          ) : (
                            <>
                              <User className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              Reactivate
                            </>
                          )}
                        </button>
                        <button
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 hover:text-red-700 transition-all last:rounded-b-xl border-t border-gray-100 dark:border-gray-700"
                          onClick={() => handleAction(student.id, 'delete')}
                        >
                          <Trash2 className="w-4 h-4 flex-shrink-0" />
                          { 'Delete'+ ' ' + account_type }
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          ))
        )}
      </div>

      {/* Inline Cash Payment Modal */}
      {editingPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Euro className="w-6 h-6 text-emerald-600" />
                Cash Payment
              </h3>
              <button
                className="btn btn-ghost p-1 -m-1"
                onClick={() => setEditingPayment(null)}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Student info */}
            { account_type == 'student' && (
              <div className="card p-4 mb-6 text-center">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="font-bold text-lg">{students.find(s => s.id === editingPayment.studentId)?.first_name}</p>
                  <p className="text-text-light text-sm"> Current balance: €{balance}
                  </p>
              </div>
            )}

            {/* Amount input */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-text-light mb-2">Cash Amount (€)</label>
                <div className="relative">
                  <Euro className="w-5 h-5 text-text-light absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="50"
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-2xl bg-background text-lg text-right focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    value={editingPayment.amount}
                    onChange={(e) => setEditingPayment({
                      ...editingPayment,
                      amount: e.target.value
                    })}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                className="flex-1 btn btn-outline py-4 text-sm"
                onClick={() => setEditingPayment(null)}
                disabled={loadingIds.has(editingPayment.studentId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 btn btn-primary py-4 text-sm flex items-center justify-center gap-2"
                onClick={() => handleCashPayment(editingPayment.studentId, parseFloat(editingPayment.amount))}
                disabled={!editingPayment.amount || parseFloat(editingPayment.amount) <= 0 || loadingIds.has(editingPayment.studentId)}
              >
                {loadingIds.has(editingPayment.studentId) ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default StudentsList;
